import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import DOMPurify from "dompurify";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import emojiDataEs from "@emoji-mart/data/i18n/es.json";
import { patchEmojiDataWithSpanish } from "./emojiKeywordsEs";

// Mutate the emoji-mart data once: append Spanish keywords so the picker's
// internal search matches terms like "risa", "fuego", "corazon", etc.
patchEmojiDataWithSpanish(emojiData as Parameters<typeof patchEmojiDataWithSpanish>[0]);

const ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "span"];

const sanitizeHtml = (html: string): string => {
  const cleaned = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["style"],
    ALLOWED_URI_REGEXP: /^$/,
    KEEP_CONTENT: true,
  }) as string;
  // Strip every style declaration except text-align (block elements) and
  // font-size (emoji span wrappers).
  return cleaned.replace(/style="([^"]*)"/g, (_m, value: string) => {
    const decls: string[] = [];
    const align = value.toLowerCase().match(/text-align\s*:\s*(left|right|center|justify)/);
    if (align) decls.push(`text-align:${align[1]}`);
    const size = value.toLowerCase().match(/font-size\s*:\s*([0-9]*\.?[0-9]+)(em|rem|px)/);
    if (size) decls.push(`font-size:${size[1]}${size[2]}`);
    return decls.length ? `style="${decls.join(";")}"` : "";
  });
};

// Strip HTML tags to count actual user content length (matches backend limit).
const visibleLength = (html: string): number =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length;

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton = ({ active, disabled, onClick, title, children }: ToolbarButtonProps) => {
  const [hover, setHover] = useState(false);
  const background = disabled
    ? "transparent"
    : active
    ? "rgba(0,101,221,0.16)"
    : hover
    ? "rgba(15,23,42,0.06)"
    : "transparent";
  const color = disabled ? "#cbd5e1" : active ? "#0050b3" : hover ? "#0050b3" : "#475569";
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: "28px",
        height: "28px",
        padding: "0 7px",
        borderRadius: "6px",
        border: "1px solid transparent",
        background,
        color,
        fontFamily: "Roboto, sans-serif",
        fontSize: "12.5px",
        fontWeight: active ? 700 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 0.12s, color 0.12s",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </button>
  );
};

const Divider = () => (
  <div style={{ width: "1px", height: "16px", background: "#e2e8f0", margin: "0 4px" }} />
);

const PICKER_W = 352;
const PICKER_H = 420;

const insertEmoji = (editor: Editor, emoji: string) => {
  // Insert as plain text — wrapping in <span> would be stripped to literal
  // characters because StarterKit's schema has no <span> node. Emoji size
  // tracks the editor's base font-size, which we bumped to 16px.
  editor.chain().focus().insertContent(emoji).run();
};

const EmojiPicker = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 6;
    let left = rect.left;
    let top = rect.bottom + margin;
    if (left + PICKER_W + 8 > window.innerWidth) {
      left = Math.max(8, window.innerWidth - PICKER_W - 8);
    }
    if (top + PICKER_H + 8 > window.innerHeight) {
      top = Math.max(8, rect.top - PICKER_H - margin);
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={triggerRef} style={{ position: "relative" }}>
      <ToolbarButton title="Insertar emoji" active={open} onClick={() => setOpen((v) => !v)}>
        <span style={{ fontSize: "15px" }}>😊</span>
      </ToolbarButton>
      {open && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: "fixed",
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            zIndex: 2000,
          }}
        >
          <Picker
            data={emojiData}
            i18n={emojiDataEs}
            locale="es"
            theme="light"
            previewPosition="none"
            skinTonePosition="none"
            navPosition="top"
            searchPosition="sticky"
            perLine={9}
            maxFrequentRows={1}
            onEmojiSelect={(e: { native?: string }) => {
              if (e?.native) {
                insertEmoji(editor, e.native);
                setOpen(false);
              }
            }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

const HeadingButtons = ({ editor }: { editor: Editor }) => {
  const applyHeading = (level: 1 | 2 | 3) => {
    const { from, to, empty } = editor.state.selection;

    // Sin selección (cursor) → comportamiento normal
    if (empty) {
      editor.chain().focus().toggleHeading({ level }).run();
      return;
    }

    const { state } = editor;
    const $from = state.doc.resolve(from);
    const $to   = state.doc.resolve(to);

    // Selección multi-bloque → comportamiento normal
    if (!$from.sameParent($to)) {
      editor.chain().focus().toggleHeading({ level }).run();
      return;
    }

    const blockStart = $from.start(); // inicio del contenido del bloque padre
    const blockEnd   = $from.end();   // fin del contenido del bloque padre

    // Todo el bloque está seleccionado → comportamiento normal
    if (from <= blockStart && to >= blockEnd) {
      editor.chain().focus().toggleHeading({ level }).run();
      return;
    }

    // ── Selección parcial dentro de un único bloque ──────────────────────────
    // Dividimos el bloque en los bordes de la selección y aplicamos el heading
    // solo a la porción seleccionada.
    const headingType   = state.schema.nodes.heading;
    const paragraphType = state.schema.nodes.paragraph;
    if (!headingType || !paragraphType) {
      editor.chain().focus().toggleHeading({ level }).run();
      return;
    }

    // Toggle: si el bloque ya ES ese heading, volvemos a párrafo
    const isAlreadyHeading =
      $from.parent.type === headingType && $from.parent.attrs.level === level;
    const targetType  = isAlreadyHeading ? paragraphType : headingType;
    const targetAttrs = isAlreadyHeading ? {} : { level };

    const { tr } = state;

    // 1. Dividir en el borde derecho primero → no afecta las posiciones ≤ to
    if (to < blockEnd) tr.split(to);

    // 2. Dividir en el borde izquierdo, especificando el tipo del nuevo bloque
    if (from > blockStart) {
      tr.split(from, 1, [{ type: targetType, attrs: targetAttrs }]);
    } else {
      // La selección empieza al inicio del bloque: cambiar tipo in-place
      tr.setNodeMarkup($from.before($from.depth), targetType, targetAttrs);
    }

    editor.view.dispatch(tr);
  };

  const levels: Array<{ level: 1 | 2 | 3; label: string }> = [
    { level: 1, label: "H1" },
    { level: 2, label: "H2" },
    { level: 3, label: "H3" },
  ];

  return (
    <>
      {levels.map(({ level, label }) => (
        <ToolbarButton
          key={level}
          active={editor.isActive("heading", { level })}
          onClick={() => applyHeading(level)}
          title={`Título ${level}`}
        >
          {label}
        </ToolbarButton>
      ))}
    </>
  );
};

const Icon = ({ d, stroke = "currentColor" }: { d: React.ReactNode; stroke?: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);

const IconBulletList = (
  <Icon
    d={
      <>
        <line x1="9" y1="6" x2="20" y2="6" />
        <line x1="9" y1="12" x2="20" y2="12" />
        <line x1="9" y1="18" x2="20" y2="18" />
        <circle cx="5" cy="6" r="1.2" fill="currentColor" />
        <circle cx="5" cy="12" r="1.2" fill="currentColor" />
        <circle cx="5" cy="18" r="1.2" fill="currentColor" />
      </>
    }
  />
);

const IconOrderedList = (
  <Icon
    d={
      <>
        <line x1="10" y1="6" x2="20" y2="6" />
        <line x1="10" y1="12" x2="20" y2="12" />
        <line x1="10" y1="18" x2="20" y2="18" />
        <text x="3" y="8" fontSize="6" fill="currentColor" stroke="none" fontFamily="Arial">1</text>
        <text x="3" y="14" fontSize="6" fill="currentColor" stroke="none" fontFamily="Arial">2</text>
        <text x="3" y="20" fontSize="6" fill="currentColor" stroke="none" fontFamily="Arial">3</text>
      </>
    }
  />
);

const IconAlignLeft = (
  <Icon
    d={
      <>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="4" y1="12" x2="14" y2="12" />
        <line x1="4" y1="18" x2="18" y2="18" />
        <line x1="4" y1="9" x2="16" y2="9" />
        <line x1="4" y1="15" x2="12" y2="15" />
      </>
    }
  />
);

const IconAlignCenter = (
  <Icon
    d={
      <>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="5" y1="18" x2="19" y2="18" />
        <line x1="6" y1="9" x2="18" y2="9" />
        <line x1="8" y1="15" x2="16" y2="15" />
      </>
    }
  />
);

const IconAlignRight = (
  <Icon
    d={
      <>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="10" y1="12" x2="20" y2="12" />
        <line x1="6" y1="18" x2="20" y2="18" />
        <line x1="8" y1="9" x2="20" y2="9" />
        <line x1="12" y1="15" x2="20" y2="15" />
      </>
    }
  />
);

const IconAlignJustify = (
  <Icon
    d={
      <>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="4" y1="9" x2="20" y2="9" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="15" x2="20" y2="15" />
        <line x1="4" y1="18" x2="20" y2="18" />
      </>
    }
  />
);

const AlignButtons = ({ editor }: { editor: Editor }) => {
  const aligns: Array<{ value: "left" | "center" | "right" | "justify"; icon: React.ReactNode; title: string }> = [
    { value: "left", icon: IconAlignLeft, title: "Alinear a la izquierda" },
    { value: "center", icon: IconAlignCenter, title: "Centrar" },
    { value: "right", icon: IconAlignRight, title: "Alinear a la derecha" },
    { value: "justify", icon: IconAlignJustify, title: "Justificar" },
  ];
  const currentlyAligned = (v: string) => editor.isActive({ textAlign: v });
  // Default is "left" when no alignment attr is present.
  const noneActive = !aligns.some((a) => currentlyAligned(a.value));
  return (
    <>
      {aligns.map(({ value, icon, title }) => (
        <ToolbarButton
          key={value}
          active={currentlyAligned(value) || (value === "left" && noneActive)}
          onClick={() => editor.chain().focus().setTextAlign(value).run()}
          title={title}
        >
          {icon}
        </ToolbarButton>
      ))}
    </>
  );
};

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  maxLength: number;
  placeholder?: string;
  minHeight?: number;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  maxLength,
  placeholder = "Escribe aquí…",
  minHeight = 160,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = sanitizeHtml(editor.getHTML());
      onChange(html);
    },
    editorProps: {
      attributes: {
        style: `min-height:${minHeight}px;outline:none;font-family:Roboto,sans-serif;font-size:15px;color:#1e293b;line-height:1.6;padding:12px 14px;`,
      },
      // Impedir que se fusionen bloques normales DENTRO de un heading al hacer
      // Backspace/Delete, lo que convertiría texto normal en título.
      handleKeyDown: (view, event) => {
        const { state } = view;
        const { $from, empty } = state.selection;
        const headingType = state.schema.nodes.heading;

        // Solo manejar cursor puro (sin selección) en bloques de nivel raíz
        if (!empty || $from.depth !== 1) return false;

        // ── Backspace al inicio de un bloque no-heading ──────────────────────
        // Si el bloque anterior es un heading, mover cursor ahí sin fusionar.
        if (
          event.key === "Backspace" &&
          $from.parentOffset === 0 &&
          $from.parent.type !== headingType
        ) {
          const blockNodePos = $from.before(1);
          if (blockNodePos > 0) {
            const $prev = state.doc.resolve(blockNodePos - 1);
            if ($prev.parent.type === headingType) {
              view.dispatch(state.tr.setSelection(TextSelection.near($prev, -1)));
              return true; // consumir el evento: no fusionar
            }
          }
        }

        // ── Delete al final de un heading ────────────────────────────────────
        // Si hay un bloque debajo, mover cursor al inicio de ese bloque sin
        // absorber su contenido dentro del heading.
        if (
          event.key === "Delete" &&
          $from.parent.type === headingType &&
          $from.parentOffset === $from.parent.content.size
        ) {
          const afterPos = $from.after(1);
          if (afterPos < state.doc.content.size) {
            const $next = state.doc.resolve(afterPos + 1);
            view.dispatch(state.tr.setSelection(TextSelection.near($next, 1)));
            return true; // consumir el evento: no fusionar
          }
        }

        return false;
      },
    },
  });

  // useEditor in @tiptap/react v3 no longer re-renders on every transaction.
  // Force a re-render on selection/transaction so toolbar `isActive` queries
  // (heading, alignment, bold...) reflect state immediately, not after the
  // next keystroke.
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => forceUpdate();
    editor.on("transaction", handler);
    editor.on("selectionUpdate", handler);
    editor.on("focus", handler);
    editor.on("blur", handler);
    return () => {
      editor.off("transaction", handler);
      editor.off("selectionUpdate", handler);
      editor.off("focus", handler);
      editor.off("blur", handler);
    };
  }, [editor]);

  // Keep editor content in sync when parent value changes externally (e.g. hydration).
  useEffect(() => {
    if (!editor) return;
    const current = sanitizeHtml(editor.getHTML());
    const incoming = sanitizeHtml(value || "");
    if (current !== incoming) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const currentLength = visibleLength(editor.getHTML());
  const overLimit = currentLength > maxLength;

  return (
    <div
      style={{
        border: `1px solid ${overLimit ? "#dc2626" : "#e2e8f0"}`,
        borderRadius: "10px",
        background: "#fff",
      }}
    >
      <style>{`
        .rte-content .ProseMirror p { margin: 0 0 6px 0; }
        .rte-content .ProseMirror p:last-child { margin-bottom: 0; }
        .rte-content .ProseMirror h1 { font-size: 19px; font-weight: 700; margin: 6px 0; }
        .rte-content .ProseMirror h2 { font-size: 16px; font-weight: 700; margin: 6px 0; }
        .rte-content .ProseMirror h3 { font-size: 14px; font-weight: 700; margin: 6px 0; }
        .rte-content .ProseMirror ul { list-style: disc; padding-left: 22px; margin: 4px 0; }
        .rte-content .ProseMirror ol { list-style: decimal; padding-left: 22px; margin: 4px 0; }
        .rte-content .ProseMirror li { margin: 2px 0; }
        .rte-content .ProseMirror p.is-editor-empty:first-child::before {
          color: #94a3b8;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>

      {/* Toolbar — sticky at top of scroll container so it stays visible
          while the recruiter scrolls long descriptions inside the modal. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "2px",
          padding: "6px 8px",
          borderBottom: "1px solid #f1f5f9",
          background: "#f8fafc",
          borderTopLeftRadius: "9px",
          borderTopRightRadius: "9px",
        }}
      >
        <HeadingButtons editor={editor} />
        <Divider />
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negrita"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Itálica"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Subrayado"
        >
          <span style={{ textDecoration: "underline" }}>U</span>
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Lista con viñetas"
        >
          {IconBulletList}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Lista numerada"
        >
          {IconOrderedList}
        </ToolbarButton>
        <Divider />
        <AlignButtons editor={editor} />
        <Divider />
        <EmojiPicker editor={editor} />
      </div>

      <div className="rte-content">
        <EditorContent editor={editor} data-placeholder={placeholder} />
      </div>

      {/* Char count */}
      <div
        style={{
          padding: "6px 12px",
          borderTop: "1px solid #f1f5f9",
          background: "#fafafa",
          textAlign: "right",
          fontFamily: "Roboto, sans-serif",
          fontSize: "11px",
          color: overLimit ? "#dc2626" : "#94a3b8",
          fontWeight: overLimit ? 600 : 400,
          borderBottomLeftRadius: "9px",
          borderBottomRightRadius: "9px",
        }}
      >
        {currentLength.toLocaleString("es-CL")} / {maxLength.toLocaleString("es-CL")}
      </div>
    </div>
  );
};

export default RichTextEditor;
